import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getProductionPlan,
  importProductionPlan,
} from '../../../api/production-planApi';
import { getApiErrorMessage } from '../../../api/mix-deliveriesApi';

export default function ProductionPlanPanel({ canWrite }) {
  const [data, setData] = useState({ import: null, items: [], total: 0 });
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const input = useRef(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getProductionPlan({
      page: page + 1,
      limit: 25,
      search: search || undefined,
    })
      .then((result) => {
        if (active) {
          const last = Math.max(0, Math.ceil(result.total / 25) - 1);
          if (page > last) setPage(last);
          else setData(result);
        }
      })
      .catch((e) => {
        if (active) setError(getApiErrorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, search, refresh]);
  const upload = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await importProductionPlan(file);
      setMessage(
        `Zaimportowano ${result.rowCount} zleceń. Pominięto ${Object.values(
          result.skipped,
        ).reduce(
          (a, b) => a + b,
          0,
        )} wierszy. Przelicz transporty, aby uwzględnić nowy plan.`,
      );
      setFile(null);
      if (input.current) input.current.value = '';
      setPage(0);
      setSearch('');
      setSearchInput('');
      setRefresh((v) => v + 1);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Plan produkcji</Typography>
        <Typography>
          Plan wskazuje możliwość uzupełnienia braków. Nie zwiększa dostępnego
          zapasu. Nowy import zastępuje bieżący plan tego magazynu; linia LS1 i
          pozycje w litrach są pomijane.
        </Typography>
        {canWrite && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <input
              type="file"
              accept=".xlsx"
              aria-label="Plik planu produkcji"
              ref={input}
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button disabled={busy || !file} onClick={upload}>
              Importuj plan produkcji
            </Button>
          </Stack>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="success">{message}</Alert>}
        {loading ? (
          <Typography>Wczytywanie planu…</Typography>
        ) : (
          !error && (
            <>
              <Typography>
                {data.import
                  ? `Bieżący plan: ${data.import.fileName} · import ${new Date(
                      data.import.createdAt,
                    ).toLocaleString('pl-PL')}`
                  : 'Brak planu produkcji. Symulacja korzysta wyłącznie z zapasu.'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Zlecenie, materiał lub linia"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearch(searchInput.trim());
                      setPage(0);
                    }
                  }}
                />
                <Button
                  onClick={() => {
                    setSearch(searchInput.trim());
                    setPage(0);
                  }}
                >
                  Szukaj w planie
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small" aria-label="Plan produkcji">
                  <TableHead>
                    <TableRow>
                      {[
                        'Zlecenie / rodzaj',
                        'Materiał',
                        'Zakład / linia',
                        'Planowany start',
                        'Cała planowana ilość',
                      ].map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map((order) => (
                      <TableRow key={order.orderNumber}>
                        <TableCell>
                          {order.orderNumber} ·{' '}
                          {order.orderType === 'PROCESS'
                            ? 'Procesowe'
                            : 'Planowane'}
                        </TableCell>
                        <TableCell>
                          {order.materialNumber} · {order.description}
                        </TableCell>
                        <TableCell>
                          {order.plant} / {order.line}
                        </TableCell>
                        <TableCell>
                          {order.startsAt.replace('T', ' ')}
                        </TableCell>
                        <TableCell>
                          {order.quantity.toLocaleString('pl-PL')} {order.unit}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )
        )}
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          rowsPerPage={25}
          rowsPerPageOptions={[25]}
          onPageChange={(_, p) => setPage(p)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} z ${count}`
          }
          backIconButtonProps={{ disabled: loading || page === 0 }}
          nextIconButtonProps={{
            disabled: loading || (page + 1) * 25 >= data.total,
          }}
        />
      </Stack>
    </Paper>
  );
}
